import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import type { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { DotSeparator, TypographyPageTitle, TypographySubtle } from '@seen/ui'
import {
  forwardRef,
  type JSX,
  type ReactNode,
  useContext,
  useState,
} from 'react'
import { z } from 'zod'
import { ExternalTextLink } from '#src/components/core/ExternalTextLink.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import type { InputProps } from '#src/components/core/input/Input.tsx'
import { NumberInput } from '#src/components/core/NumberInput.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolMoreDropdownMenu } from '#src/components/validated-tool-form/shared/ValidatedToolMoreDropdownMenu.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'
import { CurrentParticipantContext } from '#src/contexts/index.ts'
import { useDidChange } from '#src/hooks/useDidChange.ts'

export interface ENNFormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  disabled?: boolean
  restorationId?: string
}

const schema = z.object({
  weight: z.number().nullable(),
  height: z.number().nullable(),
  'usual-body-weight': z.number().nullable(),
  'goal-weight': z.number().nullable(),
  'calorie-goal-min': z.number().nullable(),
  'calorie-goal-max': z.number().nullable(),
  'protein-goal-min': z.number().nullable(),
  'protein-goal-max': z.number().nullable(),
})

const validatedTool = new ValidatedTool(questionnaires.ennQuestionnaire)

export function ENNForm({
  response,
  disabled,
  restorationId,
  ...delegated
}: ENNFormProps): JSX.Element {
  return response ? (
    <ENNEditor response={response} disabled={disabled} />
  ) : (
    <ENNCreateForm
      disabled={disabled}
      restorationId={restorationId}
      {...delegated}
    />
  )
}

function ENNEditor({
  response,
  disabled,
}: {
  response: ValidatedToolResponse
  disabled?: boolean
}): ReactNode {
  const weight = response.getAnswerValueNumber('weight')
  const height = response.getAnswerValueNumber('height')
  const goalWeight = response.getAnswerValueNumber('goal-weight')
  const calorieGoalMin = response.getAnswerValueNumber('calorie-goal-min')
  const calorieGoalMax = response.getAnswerValueNumber('calorie-goal-max')
  const proteinGoalMin = response.getAnswerValueNumber('protein-goal-min')
  const proteinGoalMax = response.getAnswerValueNumber('protein-goal-max')
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-1">
          <TypographyPageTitle>{validatedTool.name}</TypographyPageTitle>
          <ValidatedToolMoreDropdownMenu
            validatedTool={validatedTool}
            response={response}
          />
        </div>
      </div>
      <ValidatedToolSection title="Weight">
        <div className="flex flex-wrap gap-10">
          <div className="flex flex-col gap-2">
            <TypographySubtle>Measured</TypographySubtle>
            <ResourceFieldForm
              instance={response}
              valueFn={(r) => r.getAnswerValueNumber('weight')}
              patchFn={(r, v) => {
                r.setAnswer(validatedTool.getItem('weight'), v)
              }}
              disabled={disabled}
              debounceTyping
              render={({ field: { value, onChange, ...rest } }) => (
                <WeightInput value={value} onChange={onChange} {...rest} />
              )}
            />
          </div>
          <div className="flex flex-col gap-2">
            <TypographySubtle>Usual</TypographySubtle>
            <ResourceFieldForm
              instance={response}
              valueFn={(r) => r.getAnswerValueNumber('usual-body-weight')}
              patchFn={(r, v) => {
                r.setAnswer(validatedTool.getItem('usual-body-weight'), v)
              }}
              disabled={disabled}
              debounceTyping
              render={({ field: { value, onChange, ...rest } }) => (
                <WeightInput value={value} onChange={onChange} {...rest} />
              )}
            />
          </div>
        </div>
      </ValidatedToolSection>
      <ValidatedToolSection title="Height">
        <ResourceFieldForm
          instance={response}
          valueFn={(r) => r.getAnswerValueNumber('height')}
          patchFn={(r, v) => {
            r.setAnswer(validatedTool.getItem('height'), v)
          }}
          disabled={disabled}
          debounceTyping
          render={({ field: { value, onChange, ...rest } }) => (
            <HeightInput value={value} onChange={onChange} {...rest} />
          )}
        />
      </ValidatedToolSection>
      <BMISection weight={weight} height={height} />
      <IdealBodyWeightSection height={height} />
      <ValidatedToolSection title="Goal Weight">
        <ResourceFieldForm
          instance={response}
          valueFn={(r) => r.getAnswerValueNumber('goal-weight')}
          patchFn={(r, v) => {
            r.setAnswer(validatedTool.getItem('goal-weight'), v)
          }}
          disabled={disabled}
          debounceTyping
          render={({ field: { value, onChange, ...rest } }) => (
            <WeightInput value={value} onChange={onChange} {...rest} />
          )}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Calorie Goal">
        <ResourceFieldForm
          instance={response}
          valueFn={(r) => r.getAnswerValueNumber('calorie-goal-min')}
          patchFn={(r, v) => {
            r.setAnswer(validatedTool.getItem('calorie-goal-min'), v)
          }}
          disabled={disabled}
          debounceTyping
          render={({ field }) => (
            <div className="flex items-center gap-2">
              Min:
              <NumberInput {...field} className="max-w-24" />
              <TypographySubtle className="text-foreground-secondary">
                kcal/kg × {goalWeight ? `${goalWeight} kg` : 'goal weight'} =
              </TypographySubtle>
              {!!goalWeight && (
                <p>{Math.round((calorieGoalMin ?? 0) * goalWeight)} kcal</p>
              )}
              {!goalWeight && <p>Missing goal weight.</p>}
            </div>
          )}
        />
        <ResourceFieldForm
          instance={response}
          valueFn={(r) => r.getAnswerValueNumber('calorie-goal-max')}
          patchFn={(r, v) => {
            r.setAnswer(validatedTool.getItem('calorie-goal-max'), v)
          }}
          disabled={disabled}
          debounceTyping
          render={({ field }) => (
            <div className="flex items-center gap-2">
              Max:
              <NumberInput {...field} className="max-w-24" />
              <TypographySubtle className="text-foreground-secondary">
                kcal/kg × {goalWeight ? `${goalWeight} kg` : 'goal weight'} =
              </TypographySubtle>
              {!!goalWeight && (
                <p>{Math.round((calorieGoalMax ?? 0) * goalWeight)} kcal</p>
              )}
              {!goalWeight && <p>Missing goal weight.</p>}
            </div>
          )}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Protein Goal">
        <ResourceFieldForm
          instance={response}
          valueFn={(r) => r.getAnswerValueNumber('protein-goal-min')}
          patchFn={(r, v) => {
            r.setAnswer(validatedTool.getItem('protein-goal-min'), v)
          }}
          disabled={disabled}
          debounceTyping
          render={({ field }) => (
            <div className="flex items-center gap-2">
              Min:
              <NumberInput {...field} className="max-w-24" />
              <TypographySubtle className="text-foreground-secondary">
                g/kg × {goalWeight ? `${goalWeight} kg` : 'goal weight'} =
              </TypographySubtle>
              {!!goalWeight && (
                <p>{Math.round((proteinGoalMin ?? 0) * goalWeight)} g</p>
              )}
              {!goalWeight && <p>Missing goal weight.</p>}
            </div>
          )}
        />
        <ResourceFieldForm
          instance={response}
          valueFn={(r) => r.getAnswerValueNumber('protein-goal-max')}
          patchFn={(r, v) => {
            r.setAnswer(validatedTool.getItem('protein-goal-max'), v)
          }}
          disabled={disabled}
          debounceTyping
          render={({ field }) => (
            <div className="flex items-center gap-2">
              Max:
              <NumberInput {...field} className="max-w-24" />
              <TypographySubtle className="text-foreground-secondary">
                g/kg × {goalWeight ? `${goalWeight} kg` : 'goal weight'} =
              </TypographySubtle>
              {!!goalWeight && (
                <p>{Math.round((proteinGoalMax ?? 0) * goalWeight)} g</p>
              )}
              {!goalWeight && <p>Missing goal weight.</p>}
            </div>
          )}
        />
      </ValidatedToolSection>
    </div>
  )
}

function ENNCreateForm({
  disabled,
  restorationId,
  ...delegated
}: Omit<ValidatedToolInnerFormProps, 'validatedTool' | 'form' | 'subtitle'> & {
  disabled?: boolean
  restorationId?: string
}): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    disabled,
    restorationId,
  })

  const weight = form.watch('weight')
  const height = form.watch('height')
  const goalWeight = form.watch('goal-weight')
  const calorieGoalMin = form.watch('calorie-goal-min')
  const calorieGoalMax = form.watch('calorie-goal-max')
  const proteinGoalMin = form.watch('protein-goal-min')
  const proteinGoalMax = form.watch('protein-goal-max')

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      form={form}
      {...delegated}
    >
      <ValidatedToolSection title="Weight">
        <div className="flex flex-wrap gap-10">
          <div className="flex flex-col gap-2">
            <TypographySubtle>Measured</TypographySubtle>
            <FormField
              control={form.control}
              name="weight"
              render={({ field: { value, onChange, ...rest } }) => (
                <WeightInput value={value} onChange={onChange} {...rest} />
              )}
            />
          </div>
          <div className="flex flex-col gap-2">
            <TypographySubtle>Usual</TypographySubtle>
            <FormField
              control={form.control}
              name="usual-body-weight"
              render={({ field: { value, onChange, ...rest } }) => (
                <WeightInput value={value} onChange={onChange} {...rest} />
              )}
            />
          </div>
        </div>
      </ValidatedToolSection>
      <ValidatedToolSection title="Height">
        <FormField
          control={form.control}
          name="height"
          render={({ field: { value, onChange, ...rest } }) => (
            <HeightInput value={value} onChange={onChange} {...rest} />
          )}
        />
      </ValidatedToolSection>
      <BMISection weight={weight} height={height} />
      <IdealBodyWeightSection height={height} />
      <ValidatedToolSection title="Goal Weight">
        <FormField
          control={form.control}
          name="goal-weight"
          render={({ field: { value, onChange, ...rest } }) => (
            <WeightInput value={value} onChange={onChange} {...rest} />
          )}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Calorie Goal">
        <FormField
          control={form.control}
          name="calorie-goal-min"
          render={({ field }) => (
            <div className="flex items-center gap-2">
              Min:
              <NumberInput {...field} className="max-w-24" />
              <TypographySubtle className="text-foreground-secondary">
                kcal/kg × {goalWeight ? `${goalWeight} kg` : 'goal weight'} =
              </TypographySubtle>
              {!!goalWeight && (
                <p>{Math.round((calorieGoalMin ?? 0) * goalWeight)} kcal</p>
              )}
              {!goalWeight && <p>Missing goal weight.</p>}
            </div>
          )}
        />
        <FormField
          control={form.control}
          name="calorie-goal-max"
          render={({ field }) => (
            <div className="flex items-center gap-2">
              Max:
              <NumberInput {...field} className="max-w-24" />
              <TypographySubtle className="text-foreground-secondary">
                kcal/kg × {goalWeight ? `${goalWeight} kg` : 'goal weight'} =
              </TypographySubtle>
              {!!goalWeight && (
                <p>{Math.round((calorieGoalMax ?? 0) * goalWeight)} kcal</p>
              )}
              {!goalWeight && <p>Missing goal weight.</p>}
            </div>
          )}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Protein Goal">
        <FormField
          control={form.control}
          name="protein-goal-min"
          render={({ field }) => (
            <div className="flex items-center gap-2">
              Min:
              <NumberInput {...field} className="max-w-24" />
              <TypographySubtle className="text-foreground-secondary">
                g/kg × {goalWeight ? `${goalWeight} kg` : 'goal weight'} =
              </TypographySubtle>
              {!!goalWeight && (
                <p>{Math.round((proteinGoalMin ?? 0) * goalWeight)} g</p>
              )}
              {!goalWeight && <p>Missing goal weight.</p>}
            </div>
          )}
        />
        <FormField
          control={form.control}
          name="protein-goal-max"
          render={({ field }) => (
            <div className="flex items-center gap-2">
              Max:
              <NumberInput {...field} className="max-w-24" />
              <TypographySubtle className="text-foreground-secondary">
                g/kg × {goalWeight ? `${goalWeight} kg` : 'goal weight'} =
              </TypographySubtle>
              {!!goalWeight && (
                <p>{Math.round((proteinGoalMax ?? 0) * goalWeight)} g</p>
              )}
              {!goalWeight && <p>Missing goal weight.</p>}
            </div>
          )}
        />
      </ValidatedToolSection>
      <div>
        <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
      </div>
    </ValidatedToolInnerForm>
  )
}

interface WeightInputProps extends Omit<InputProps, 'value' | 'onChange'> {
  value: number | null
  onChange: (value: number | null) => void
}

const WeightInput = forwardRef<HTMLInputElement, WeightInputProps>(
  ({ value, onChange, disabled, ...delegated }, ref) => {
    const lbsFromValue =
      value === null ? null : Math.round(value * KG_TO_LBS * 10) / 10
    const [lbs, setLbs] = useState(lbsFromValue)
    const [isLbsFocused, setIsLbsFocused] = useState(false)
    const didValueChange = useDidChange(value)
    if (didValueChange && !isLbsFocused) {
      setLbs(lbsFromValue)
    }

    return (
      <div className="grid gap-2">
        <div className="flex items-center gap-2">
          <NumberInput
            value={value}
            onChange={(v) => {
              onChange(v)
              setLbs(v === null ? null : Math.round(v * KG_TO_LBS * 10) / 10)
            }}
            className="max-w-24"
            ref={ref}
            disabled={disabled}
            {...delegated}
          />
          kg
        </div>
        <div className="flex items-center gap-2">
          <NumberInput
            value={lbs}
            onChange={(v) => {
              setLbs(v)
              onChange(
                v === null ? null : Math.round((v / KG_TO_LBS) * 100) / 100 // We want to ensure values such as 174.5 lb are stored with enough precision in kg to be accurately displayed when they're converted back to lbs, but we don't want to store too many significant digits that don't properly represent the accuracy of the measurement.
              )
            }}
            className="max-w-24"
            disabled={disabled}
            onFocus={() => setIsLbsFocused(true)}
            onBlur={() => setIsLbsFocused(false)}
          />
          lbs
        </div>
      </div>
    )
  }
)
WeightInput.displayName = 'WeightInput'

const KG_TO_LBS = 2.20462

interface HeightInputProps extends Omit<InputProps, 'value' | 'onChange'> {
  value: number | null
  onChange: (value: number | null) => void
}

const HeightInput = forwardRef<HTMLInputElement, HeightInputProps>(
  ({ value, onChange, disabled, ...delegated }, ref) => {
    const inchesFromValue =
      value === null ? null : Math.round(value * CM_TO_INCHES * 10) / 10
    const [inches, setInches] = useState(inchesFromValue)
    const [isInchesFocused, setIsInchesFocused] = useState(false)
    const didValueChange = useDidChange(value)
    if (didValueChange && !isInchesFocused) {
      setInches(inchesFromValue)
    }

    return (
      <div className="grid gap-2">
        <div className="flex items-center gap-2">
          <NumberInput
            value={value}
            onChange={(v) => {
              onChange(v)
              setInches(
                v === null ? null : Math.round(v * CM_TO_INCHES * 10) / 10
              )
            }}
            className="max-w-24"
            ref={ref}
            disabled={disabled}
            {...delegated}
          />
          cm
        </div>
        <div className="flex items-center gap-2">
          <NumberInput
            value={inches}
            onChange={(v) => {
              setInches(v)
              onChange(
                v === null ? null : Math.round((v / CM_TO_INCHES) * 100) / 100 // We want to ensure values are stored with enough precision in cm to be accurately displayed when they're converted back to inches, but we don't want to store too many significant digits that don't properly represent the accuracy of the measurement.
              )
            }}
            className="max-w-24"
            disabled={disabled}
            onFocus={() => setIsInchesFocused(true)}
            onBlur={() => setIsInchesFocused(false)}
          />
          in
        </div>
      </div>
    )
  }
)
HeightInput.displayName = 'HeightInput'

const CM_TO_INCHES = 0.393701

function idealBodyWeight(height: number, sex: 'male' | 'female'): number {
  // From https://scymed.com/en/smnxpn/pndfc237.htm
  const idealWeightInLbs =
    sex === 'male'
      ? 106 + Math.max(0, height * CM_TO_INCHES - 60) * 6
      : 100 + Math.max(0, height * CM_TO_INCHES - 60) * 5
  return idealWeightInLbs / KG_TO_LBS
}

function bmi(weight: number, height: number): number {
  return weight / (height / 100) ** 2
}

function BMISection({
  weight,
  height,
}: {
  weight: number | null
  height: number | null
}): ReactNode {
  return (
    <ValidatedToolSection
      title="BMI"
      subtitle={
        <>
          &lt;18.5 is below healthy range, 18.5-22.9 is within healthy range,
          23.0-26.9 is above healthy range, ≥27.0 is further above healthy
          range.{' '}
          <ExternalTextLink href="https://onlinelibrary.wiley.com/doi/10.1002/oby.23639">
            Reference
          </ExternalTextLink>
        </>
      }
    >
      {!!weight && !!height && (
        <p>
          {bmi(weight, height).toFixed(1)} kg/m² <DotSeparator />
          {bmi(weight, height) < 18.5 && <span>Below healthy range</span>}
          {bmi(weight, height) >= 18.5 && bmi(weight, height) < 23 && (
            <span>Within healthy range</span>
          )}
          {bmi(weight, height) >= 23 && bmi(weight, height) < 27 && (
            <span>Above healthy range</span>
          )}
          {bmi(weight, height) >= 27 && (
            <span>Further above healthy range</span>
          )}
        </p>
      )}
      {(!weight || !height) && <p>Missing weight and height.</p>}
    </ValidatedToolSection>
  )
}

function IdealBodyWeightSection({
  height,
}: {
  height: number | null
}): ReactNode {
  const participant = useContext(CurrentParticipantContext)
  return (
    <ValidatedToolSection
      title="Ideal Body Weight"
      subtitle={
        participant?.gender === 'male'
          ? 'Hamwi method – male: 106lb for the first 5 feet + 6 lb for each additional inch'
          : 'Hamwi method – female: 100lb for the first 5 feet + 5 lb for each additional inch'
      }
    >
      {!!height && (
        <p>
          {Math.round(
            idealBodyWeight(
              height,
              participant?.gender === 'male' ? 'male' : 'female'
            )
          )}{' '}
          kg /{' '}
          {Math.round(
            idealBodyWeight(
              height,
              participant?.gender === 'male' ? 'male' : 'female'
            ) * KG_TO_LBS
          )}{' '}
          lbs
        </p>
      )}
      {!height && <p>Missing height.</p>}
    </ValidatedToolSection>
  )
}
